import axios, { CancelToken } from 'axios';
import { getInitialProductDetailsState } from 'features/commerce/product-details/types';
import RocHawkConfig from 'features/search/RocHawkConfig';
import {
	HawksearchAttributes,
	selectionsToHawkAttributes,
} from 'features/tracking/trackers/commerce/hawksearch/hawk-search-commerce-tracker';
import { ContextDictionary, WidgetUid } from 'models/Recommendation/Request';
import { Response as RecommendationResponse } from 'models/Recommendation/Response';
import HawkClient from 'net/HawkClient';
import React, { useEffect, useState } from 'react';
import ReactDOM from 'react-dom';
import AxiosHelper from 'services/AxiosHelper';
import {
	createGuid,
	getCookie,
	getVisitExpiry,
	getVisitorExpiry,
	setCookie,
} from 'shared/components/hawksearch/helpers/Cookies';
import HawkRecommendation from 'shared/components/hawksearch/helpers/hawkRecommendationContext';
import { Widget } from 'shared/components/hawksearch/models/Recommendation/Widget';
import Spinner from 'shared/components/Spinner';
import useRocEventHandler, { dispatchRocEvent } from 'shared/hooks/useRocEventHandler';
import { Carousel } from './widgets/commerce/Carousel';
import { getCustomWidgetComponent } from './widgets/strategy';
import { WidgetProps, WidgetStrategyProps } from './widgets/types';

const initialProductState = getInitialProductDetailsState();

/**
 * Main loader component responsible for initializing portals to all elements on the page with widget guids
 * Also handles all data loading, to make sure we send all requests to the server in bulk, allowing hawk
 * to dedupe the results for us.
 */
export function Loader() {
	const config = RocHawkConfig;
	const client: HawkClient = new HawkClient(config);
	const renderHTML: boolean = false;
	const [recommendations, setRecommendations] = useState<RecommendationResponse | null>(null);
	const [loading, setLoading] = useState(true);
	//#region Commerce
	const [hawkAttributes, setHawkAttributes] = useState(selectionsToHawkAttributes(initialProductState.selections));
	//#endregion

	useEffect(() => {
		const cts = axios.CancelToken.source();

		(async () => {
			await getRecommendations(hawkAttributes, cts.token);
		})();

		return () => {
			cts.cancel();
		};
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [hawkAttributes]);

	//#region Commerce
	useRocEventHandler(
		'product-selection-updated',
		({ product }) => {
			const newHawkAttributes = selectionsToHawkAttributes(product.productDetails?.selections);
			setHawkAttributes(newHawkAttributes);
		},
		[],
	);
	//#endregion

	/**
	 * Performs an recommendation request to the Hawk API and populates the recommendation widgets set of this component.
	 */
	async function getRecommendations(hawkAttributes?: HawksearchAttributes[], cancellationToken?: CancelToken) {
		const widgetUids = getRecommendationPlaceholders();

		if (!widgetUids || widgetUids.length === 0) {
			return;
		}

		let recommendationResults: RecommendationResponse | null = null;
		let visitorId: string | null = getCookie('hawk_visit_id');
		let visitId: string | null = getCookie('hawk_visitor_id');

		const isNewVisitor = !visitId || !visitorId;

		if (!visitId) {
			setCookie('hawk_visit_id', createGuid(), getVisitExpiry());
			visitId = getCookie('hawk_visit_id');
		}

		if (!visitorId) {
			setCookie('hawk_visitor_id', createGuid(), getVisitorExpiry());
			visitorId = getCookie('hawk_visitor_id');
		}

		if (isNewVisitor) {
			AxiosHelper.post('/ajax/hawksearch/identify', {
				visitId: visitId,
				visitorId: visitorId,
			});
		}

		let contextProperties = new ContextDictionary();
		let customProperties = new ContextDictionary();

		if (window.HawkRecommendation) {
			contextProperties = HawkRecommendation.Context;
			customProperties = HawkRecommendation.CustomContext;
		}

		//#region Commerce
		// add all selections to the hawk context.
		hawkAttributes?.map((attribute) => {
			if (attribute.value) {
				contextProperties.add(`${attribute.name}`, attribute.value);
			} else {
				contextProperties.remove(attribute.name);
			}
		});
		//#endregion

		try {
			recommendationResults = await client.getRecommendationWidgetItems(
				{
					visitId,
					visitorId,
					widgetUids: widgetUids,
					clientGuid: config.clientGuid,
					enablePreview: config.isInPreview,
					renderHTML,
					contextProperties,
					customProperties,
				},
				cancellationToken,
			);

			// trigger event for widget visibility based on how many items are included
			// by default, this is used to show/hide tabs on the pdp
			widgetUids.forEach((w) => {
				if (!w.widgetGuid) {
					return;
				}

				const matchedResult = recommendationResults?.widgetItems?.find((f) => f.widgetGuid === w.widgetGuid);

				dispatchRocEvent('hawk-recs-widget-loaded', {
					widgetGuid: w.widgetGuid,
					hasItems: matchedResult ? matchedResult.recommendationItems.length > 0 : false,
				});
			});
		} catch (error) {
			if (axios.isCancel(error)) {
				return;
			}

			console.error('Recommendation widget request error:', error);

			// if something goes wrong and we can't load recs, then send events to hide all
			// each event handler component is given the choice to care about this or not.
			widgetUids.forEach((w) => {
				if (!w.widgetGuid) {
					return;
				}

				dispatchRocEvent('hawk-recs-widget-loaded', {
					widgetGuid: w.widgetGuid,
					hasItems: false,
				});
			});
		}

		setLoading(false);

		if (recommendationResults) {
			if (!recommendationResults.isSuccess) {
				console.error('Recommendation result error:', recommendationResults);
				setRecommendations(null);
			} else {
				setRecommendations(new RecommendationResponse(recommendationResults));
			}
		}
	}

	if (!recommendations || !recommendations.widgetItems || recommendations.widgetItems.length === 0) {
		// nothing to render
		return null;
	}

	return (
		<React.Suspense fallback={<Spinner />}>
			{recommendations.widgetItems.map((widget, index) => {
				const targetElements = document.querySelectorAll(`[data-widgetguid='${widget.widgetGuid}']`);

				if (targetElements.length === 0) {
					console.error(`No matching hawk recommendation widget was found in the DOM (${widget.widgetGuid})`);

					return null;
				}

				return (
					<React.Fragment key={index}>
						{Array.from(targetElements).map((el, index) => (
							<WidgetContainer
								key={index}
								element={el}
								loading={loading}
								widget={widget}
								requestId={recommendations.requestId}
								attributes={el.attributes}
							/>
						))}
					</React.Fragment>
				);
			})}
		</React.Suspense>
	);
}

interface WidgetContainerProps {
	element: Element;
	attributes: NamedNodeMap;
	loading: boolean;
	widget: Widget;
	renderHtml?: boolean;
	requestId: string;
}

/**
 * Adds a layer to easily return portals to multiple components as this cannot be done from a single component.
 */
function WidgetContainer({
	widget,
	loading,
	renderHtml = false,
	requestId,
	element,
	attributes,
}: WidgetContainerProps) {
	const WidgetComponent = getWidgetComponent({
		widgetGuid: widget.widgetGuid,
		attributes,
	});

	return ReactDOM.createPortal(
		<WidgetComponent
			widgetData={widget}
			isLoading={loading}
			renderHTML={renderHtml}
			requestId={requestId}
			attributes={attributes}
		/>,
		element,
	);
}

/**
 * This is the default strategy for picking out a widget type. It only checks if a custom one has been defined,
 * and if that returns anything, that component is returned. Otherwise, we just return the default component.
 * If you need to customize this, change `getCustomWidgetComponent`
 */
function getWidgetComponent(props: WidgetStrategyProps): React.ComponentType<WidgetProps> {
	const customComponent = getCustomWidgetComponent(props);

	if (customComponent !== undefined) {
		return customComponent;
	}

	return Carousel;
}

/**
 * Grabs all requested WidgetUids based on the elements on the page.
 * The elements match up exactly to what Hawk provides in their admin.
 */
function getRecommendationPlaceholders() {
	const widgetUids: WidgetUid[] = [];

	document.querySelectorAll('[data-widgetguid]').forEach(function (element) {
		const guid = element.getAttribute('data-widgetguid');
		if (guid) {
			widgetUids.push({
				widgetGuid: guid,
			});
		}
	});

	return widgetUids;
}
